import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Security as SecurityIcon,
  Timer as TimerIcon,
  Block as BlockIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';

interface SecurityInfoDialogProps {
  open: boolean;
  onClose: () => void;
}

export const SecurityInfoDialog: React.FC<SecurityInfoDialogProps> = ({ open, onClose }) => {
  const { t, dir } = useLanguage();
  return (
    <Dialog
      dir={dir}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          padding: '8px'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        pb: 1
      }}>
        <SecurityIcon color="primary" />
        <Typography variant="h6" component="div">
          {t('securityInfo.title')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, textAlign: 'start' }}>
        <Alert severity="info" sx={{ mb: 2, py: 0 }}>
          <Typography variant="body2">
            <strong>{t('securityInfo.protection.title')}</strong> {t('securityInfo.protection.desc')}
          </Typography>
        </Alert>

        <Typography variant="h6" sx={{ mb: 1, mt: 2, color: 'primary.main', fontSize: '1rem' }}>
          {t('securityInfo.measures.title')}
        </Typography>

        <List dense sx={{ py: 0 }}>
          <ListItem disableGutters>
            <ListItemIcon>
              <EmailIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              sx={{ textAlign: 'start' }}
              primary={t('securityInfo.measure.email.title')}
              secondary={t('securityInfo.measure.email.desc')}
            />
          </ListItem>

          <ListItem disableGutters>
            <ListItemIcon>
              <TimerIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              sx={{ textAlign: 'start' }}
              primary={t('securityInfo.measure.expiry.title')}
              secondary={t('securityInfo.measure.expiry.desc')}
            />
          </ListItem>

          <ListItem disableGutters>
            <ListItemIcon>
              <BlockIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              sx={{ textAlign: 'start' }}
              primary={t('securityInfo.measure.limit.title')}
              secondary={t('securityInfo.measure.limit.desc')}
            />
          </ListItem>

          <ListItem disableGutters>
            <ListItemIcon>
              <LockIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              sx={{ textAlign: 'start' }}
              primary={t('securityInfo.measure.abuse.title')}
              secondary={t('securityInfo.measure.abuse.desc')}
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 1 }} />

        <Typography variant="h6" sx={{ mb: 1, color: 'warning.main', fontSize: '1rem' }}>
          {t('securityInfo.help.title')}
        </Typography>

        <List dense sx={{ py: 0 }}>
          <ListItem disableGutters>
            <ListItemIcon>
              <InfoIcon color="warning" />
            </ListItemIcon>
            <ListItemText
              sx={{ textAlign: 'start' }}
              primary={t('securityInfo.help.expired.title')}
              secondary={t('securityInfo.help.expired.desc')}
            />
          </ListItem>

          <ListItem disableGutters>
            <ListItemIcon>
              <InfoIcon color="warning" />
            </ListItemIcon>
            <ListItemText
              sx={{ textAlign: 'start' }}
              primary={t('securityInfo.help.blocked.title')}
              secondary={t('securityInfo.help.blocked.desc')}
            />
          </ListItem>

          <ListItem disableGutters>
            <ListItemIcon>
              <InfoIcon color="warning" />
            </ListItemIcon>
            <ListItemText
              sx={{ textAlign: 'start' }}
              primary={t('securityInfo.help.email.title')}
              secondary={t('securityInfo.help.email.desc')}
            />
          </ListItem>
        </List>

        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>{t('securityInfo.tip.label')}</strong> {t('securityInfo.tip.desc')}
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          {t('securityInfo.btn.understand')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
